import {
  defineMessage,
  defineMessages,
  IntlShape as FormatJSIntlShape,
} from '@formatjs/intl';
import { IntlShape as ReactIntlShape } from 'react-intl';
import { faVideo } from '@fortawesome/pro-solid-svg-icons/faVideo';
import { faFileMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons/faFileMagnifyingGlass';
import { faRepeat } from '@fortawesome/pro-solid-svg-icons/faRepeat';
import { faMobile } from '@fortawesome/pro-solid-svg-icons/faMobile';
import { faCardsBlank } from '@fortawesome/pro-solid-svg-icons/faCardsBlank';
import { faGear } from '@fortawesome/pro-solid-svg-icons/faGear';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';

type IntlShape = FormatJSIntlShape | ReactIntlShape;

/**
 * ProductComponents.
 */
export const productComponents = (intl: IntlShape) => ({
  playlists: {
    title: intl.formatMessage({
      id: 'productComponents.playlists.title',
      defaultMessage: 'Playlists',
    }),
  },
  analytics: {
    title: intl.formatMessage({
      id: 'productComponents.analytics.title',
      defaultMessage: 'Admin Analytics',
    }),
  },
  workspaces: {
    title: intl.formatMessage({
      id: 'productComponents.workspaces.title',
      defaultMessage: 'Workspaces',
    }),
    subtitle: intl.formatMessage({
      id: 'productComponents.workspaces.subtitle',
      defaultMessage: 'Collaborate on coursework',
    }),
  },
  decks: {
    title: intl.formatMessage({
      id: 'productComponents.decks.title',
      defaultMessage: 'Decks',
    }),
    subtitle: intl.formatMessage({
      id: 'productComponents.decks.subtitle',
      defaultMessage: 'Write your own flashcards',
    }),
  },
  studyschedule: {
    title: intl.formatMessage({
      id: 'productComponents.studyschedule.title',
      defaultMessage: 'Study Schedule',
    }),
    subtitle: intl.formatMessage({
      id: 'productComponents.studyschedule.subtitle',
      defaultMessage: 'Organize daily to-dos',
    }),
  },
  videos: {
    title: intl.formatMessage({
      id: 'productComponents.videos.title',
      defaultMessage: 'Video Topics',
    }),
    subtitle: intl.formatMessage({
      id: 'productComponents.videos.subtitle',
      defaultMessage: 'Watch thousands of videos',
    }),
  },
  flashcards: {
    title: intl.formatMessage({
      id: 'productComponents.flashcards.title',
      defaultMessage: 'Flashcards',
    }),
    subtitle: intl.formatMessage({
      id: 'productComponents.flashcards.subtitle',
      defaultMessage: 'Practice key concepts quickly',
    }),
  },
  qbank: {
    title: intl.formatMessage({
      id: 'productComponents.questions.title',
      defaultMessage: 'QBank',
    }),
    subtitle: intl.formatMessage({
      id: 'productComponents.questions.subtitle',
      defaultMessage: 'Apply your knowledge',
    }),
  },
});

/**
 * SidebarList.
 */
export const sidebarList = (intl: IntlShape) => {
  return {
    'Video Topics': {
      title: intl.formatMessage({
        id: 'sidebarList.tools.videos',
        defaultMessage: 'Video Topics',
      }),
    },
    Playlists: {
      title: intl.formatMessage({
        id: 'sidebarList.tools.playlists',
        defaultMessage: 'Playlists',
      }),
    },
    Flashcards: {
      title: intl.formatMessage({
        id: 'sidebarList.tools.flashcards',
        defaultMessage: 'Flashcards',
      }),
    },
    Questions: {
      title: intl.formatMessage({
        id: 'sidebarList.tools.questions',
        defaultMessage: 'QBank',
      }),
    },
    'Study Schedule': {
      title: intl.formatMessage({
        id: 'sidebarList.tools.study_schedule',
        defaultMessage: 'Study Schedule',
      }),
    },
    Workspaces: {
      title: intl.formatMessage({
        id: 'sidebarList.tools.workspaces',
        defaultMessage: 'Workspaces',
      }),
    },
    Decks: {
      title: intl.formatMessage({
        id: 'sidebarList.tools.decks',
        defaultMessage: 'Decks',
      }),
    },
    'Admin Analytics': {
      title: intl.formatMessage({
        id: 'sidebarList.tools.admin_analytics',
        defaultMessage: 'Admin Analytics',
      }),
    },
  };
};

export type QuizLoggedOutItems =
  | 'Learning videos'
  | 'Expert reviewed'
  | 'Mobile app'
  | 'Flashcards'
  | 'Spaced Repetition'
  | 'Custom Quizzes';

export type LocalizedQuizLoggedOutItems = Record<
  QuizLoggedOutItems,
  { title: string; icon: IconDefinition }
>;

export const quizLoggedOutBannerItemsArray: QuizLoggedOutItems[] = [
  'Learning videos',
  'Flashcards',
  'Expert reviewed',
  'Spaced Repetition',
  'Mobile app',
  'Custom Quizzes',
];

/**
 * Quiz LoggedOut Banner.
 */
export const quizLoggedOutBannerItems = (
  intl: IntlShape,
): LocalizedQuizLoggedOutItems => {
  return {
    'Learning videos': {
      title: intl.formatMessage({
        id: 'loggedoutQuizBanner.learning_videos',
        defaultMessage: 'Learning videos',
      }),
      icon: faVideo,
    },
    Flashcards: {
      title: intl.formatMessage({
        id: 'loggedoutQuizBanner.flashcards',
        defaultMessage: 'Flashcards',
      }),
      icon: faCardsBlank,
    },
    'Expert reviewed': {
      title: intl.formatMessage({
        id: 'loggedoutQuizBanner.expert_reviewed',
        defaultMessage: 'Expert reviewed',
      }),
      icon: faFileMagnifyingGlass,
    },
    'Spaced Repetition': {
      title: intl.formatMessage({
        id: 'loggedoutQuizBanner.spaced_repetition',
        defaultMessage: 'Spaced Repetition',
      }),
      icon: faRepeat,
    },
    'Mobile app': {
      title: intl.formatMessage({
        id: 'loggedoutQuizBanner.mobile_app',
        defaultMessage: 'Mobile app',
      }),
      icon: faMobile,
    },
    'Custom Quizzes': {
      title: intl.formatMessage({
        id: 'loggedoutQuizBanner.custom_quizzes',
        defaultMessage: 'Custom Quizzes',
      }),
      icon: faGear,
    },
  };
};

/**
 * KeyboardShortcutsList.
 */
export const keyboardShortcutsList = (intl: IntlShape) => {
  return {
    'Play / Pause': {
      title: intl.formatMessage({
        id: 'video.keyboard_shortcuts_play-pause',
        defaultMessage: 'Play / Pause',
      }),
    },
    'Toggle fullscreen': {
      title: intl.formatMessage({
        id: 'video.keyboard_shortcuts_toggle-fullscreen',
        defaultMessage: 'Toggle fullscreen',
      }),
    },
    'Toggle captions': {
      title: intl.formatMessage({
        id: 'video.keyboard_shortcuts_toggle-captions',
        defaultMessage: 'Toggle captions',
      }),
    },
    'Increase playback speed': {
      title: intl.formatMessage({
        id: 'video.keyboard_shortcuts_increase-playback-speed',
        defaultMessage: 'Increase playback speed',
      }),
    },
    'Decrease playback speed': {
      title: intl.formatMessage({
        id: 'video.keyboard_shortcuts_decrease-playback-speed',
        defaultMessage: 'Decrease playback speed',
      }),
    },
    'Seek 0% - 90%': {
      title: intl.formatMessage({
        id: 'video.keyboard_shortcuts_seek0%-90%',
        defaultMessage: 'Seek 0% - 90%',
      }),
    },
    'Backward 10s': {
      title: intl.formatMessage({
        id: 'video.keyboard_shortcuts_backward-10s',
        defaultMessage: 'Backward 10s',
      }),
    },
    'Forward 10s': {
      title: intl.formatMessage({
        id: 'video.keyboard_shortcuts_forward-10s',
        defaultMessage: 'Forward 10s',
      }),
    },
    'Increase volume': {
      title: intl.formatMessage({
        id: 'video.keyboard_shortcuts_increase-volume',
        defaultMessage: 'Increase volume',
      }),
    },
    'Decrease volume': {
      title: intl.formatMessage({
        id: 'video.keyboard_shortcuts_decrease-volume',
        defaultMessage: 'Decrease volume',
      }),
    },
  };
};

/**
 * Navigation menu links.
 */
export const mainLinksObj = (intl: IntlShape) => {
  return {
    Settings: {
      title: intl.formatMessage({
        id: 'common.settings',
        defaultMessage: 'Settings',
      }),
    },
    'Help center': {
      title: intl.formatMessage({
        id: 'navigation_menu.help_center',
        defaultMessage: 'Help center',
      }),
    },
    Privacy: {
      title: intl.formatMessage({
        id: 'navigation_menu.privacy',
        defaultMessage: 'Privacy',
      }),
    },
  };
};

/**
 * Navigation menu linkGroupsTitle.
 */
export const linkGroupsTitle = (intl: IntlShape) => {
  return {
    Community: {
      title: intl.formatMessage({
        id: 'navigation_menu.community',
        defaultMessage: 'Community',
      }),
    },
    'Learning resources': {
      title: intl.formatMessage({
        id: 'navigation_menu.learning_resources',
        defaultMessage: 'Learning resources',
      }),
    },
    'Stay updated': {
      title: intl.formatMessage({
        id: 'navigation_menu.stay_updated',
        defaultMessage: 'Stay updated',
      }),
    },
    'Spread joy': {
      title: intl.formatMessage({
        id: 'navigation_menu.spread_joy',
        defaultMessage: 'Spread joy',
      }),
    },
    Company: {
      title: intl.formatMessage({
        id: 'navigation_menu.company',
        defaultMessage: 'Company',
      }),
    },
  };
};

/**
 * Navigation linkGroupsMenu.
 */
export const linkGroupsMenu = (intl: IntlShape) => {
  return {
    'Refer a friend': {
      title: intl.formatMessage({
        id: 'navigation_menu.refer_a_friend',
        defaultMessage: 'Refer a friend',
      }),
    },
    'Osmosis around the world': {
      title: intl.formatMessage({
        id: 'navigation_menu.osmosis_around_the_world',
        defaultMessage: 'Osmosis around the world',
      }),
    },
    'Help center': {
      title: intl.formatMessage({
        id: 'navigation_menu.help_centerr',
        defaultMessage: 'Help center',
      }),
    },
    Events: {
      title: intl.formatMessage({
        id: 'navigation_menu.events',
        defaultMessage: 'Events',
      }),
    },
    Blog: {
      title: intl.formatMessage({
        id: 'navigation_menu.blog',
        defaultMessage: 'Blog',
      }),
    },
    'Greetings cards': {
      title: intl.formatMessage({
        id: 'navigation_menu.greetings_cards',
        defaultMessage: 'Greetings cards',
      }),
    },
    'Swag shop': {
      title: intl.formatMessage({
        id: 'navigation_menu.swag_shop',
        defaultMessage: 'Swag shop',
      }),
    },
    'About us': {
      title: intl.formatMessage({
        id: 'navigation_menu.about_us',
        defaultMessage: 'About us',
      }),
    },
    'Editorial board': {
      title: intl.formatMessage({
        id: 'navigation_menu.latest_on_COVID.editorial_board',
        defaultMessage: 'Editorial board',
      }),
    },
    'Osmosis for institutions': {
      title: intl.formatMessage({
        id: 'navigation_menu.osmosis_for_institutions',
        defaultMessage: 'Osmosis for institutions',
      }),
    },
  };
};

/**
 * Quiz queue names.
 */
export const quizQueueAddedTime = (
  intl: IntlShape,
): Record<string, string> => ({
  'the future!': intl.formatMessage({
    id: 'quiz.queue.the_future',
    defaultMessage: 'the future!',
  }),
  'this week': intl.formatMessage({
    id: 'quiz.queue.this_week',
    defaultMessage: 'this week',
  }),
  today: intl.formatMessage({
    id: 'quiz.queue.today',
    defaultMessage: 'today',
  }),
  '1 week': intl.formatMessage({
    id: 'quiz.queue.one_week',
    defaultMessage: '1 week',
  }),
  '2 weeks': intl.formatMessage({
    id: 'quiz.queue.two_weeks',
    defaultMessage: '2 weeks',
  }),
  '1 month': intl.formatMessage({
    id: 'quiz.queue.one_month',
    defaultMessage: '1 month',
  }),
});

export const confirmationDialogHeading = defineMessages({
  submitionFailed: {
    id: 'confirmation.error.dialog.header',
    defaultMessage: 'Submission failed',
  },
  greating: {
    id: 'confirmation.dialog.header',
    defaultMessage: 'Great to hear from you!',
  },
});

export const confirmationDialogMessages = defineMessages({
  loggedOutError: {
    id: 'confirmation.dialog.logged_out_user',
    defaultMessage: 'You must be logged in to perform this action!',
  },
  thanksMessage: {
    id: 'confirmation.dialog.text',
    defaultMessage: "Thanks for contacting us, we'll be in touch shortly!",
  },
  generalError: {
    id: 'confirmation.error.dialog.text',
    defaultMessage: 'Please try again or contact us directly at {supportEmail}',
  },
});

export const quizBuilderHeaderTitle: Record<
  string,
  { id: string; defaultMessage: string }
> = defineMessages({
  startTitle: {
    id: 'quizbuilder.header.main_text',
    defaultMessage: 'Select a Quiz Builder',
  },
  studying: {
    id: 'quizbuilderlist.title',
    defaultMessage: 'What are you studying for?',
  },
});

export const snackbarMessage = defineMessage({
  id: 'terms-and-conditions.snackbar.message',
  defaultMessage:
    "In order to continue using Osmosis, you'll need to agree to our updated terms and conditions.",
});

export const billingTitles = defineMessages({
  monthly: {
    id: 'matrix_plan.monthly',
    defaultMessage: 'Monthly Billing',
  },
  yearly: {
    id: 'matrix_plan.yearly',
    defaultMessage: 'Yearly Billing',
  },
  multipleYears: {
    id: 'matrix_plan.multipleYears',
    defaultMessage: '{count} Yearly Billing',
  },
  multipleMonths: {
    id: 'matrix_plan.multipleMonths',
    defaultMessage: '{count} Month Billing',
  },
  monthlyShort: {
    id: 'matrix_plan.monthlyShort',
    defaultMessage: '1 Month',
  },
  multipleMonthsShort: {
    id: 'matrix_plan.multipleMonthsShort',
    defaultMessage: '{count} Months',
  },
  yearlyShort: {
    id: 'matrix_plan.yearlyShort',
    defaultMessage: '1 Year',
  },
  multipleYearsShort: {
    id: 'matrix_plan.multipleYearsShort',
    defaultMessage: '{count} Years',
  },
});
export const learnMetaData = defineMessages({
  desc: {
    id: 'learn_metadata_description',
    defaultMessage:
      'Videos, Flashcards, High Yield Notes, & Practice Questions. Learn and reinforce your understanding of',
  },
  osmosis: {
    id: 'learn_metadata_osmosis_name',
    defaultMessage: 'Osmosis',
  },
  video: {
    id: 'learn_metadata_video_name',
    defaultMessage: 'Video',
  },
  anatomy: {
    id: 'learn_metadata_anatomy_name',
    defaultMessage: 'Anatomy',
  },
  definition: {
    id: 'learn_metadata_definition_name',
    defaultMessage: 'Definition',
  },
  function: {
    id: 'learn_metadata_function_name',
    defaultMessage: 'Function',
  },
  library: {
    id: 'learn_metadata_osmosis-library_name',
    defaultMessage: 'Osmosis Library',
  },
});

export const languageIntlStrings: Record<
  string,
  { id: string; defaultMessage: string }
> = defineMessages({
  en: {
    id: 'language.english',
    defaultMessage: 'English',
  },
  es: {
    id: 'language.spanish',
    defaultMessage: 'Spanish',
  },
});

export const groupNames = defineMessages({
  basicsciences: {
    id: 'common.basicSciences',
    defaultMessage: 'Basic Sciences',
  },
  clinicalsciences: {
    id: 'common.clinicalSciences',
    defaultMessage: 'Clinical Sciences',
  },
});
